/**
 * Custom hook for subscribing to centralized realtime updates with debouncing.
 *
 * Usage:
 * ```tsx
 * useRealtimeSubscription(['inspections', 'defects'], () => {
 *   queryClient.invalidateQueries({ queryKey: myQueryKey });
 * });
 * ```
 *
 * Features:
 * - Subscribes to the central RealtimeProvider
 * - Debounces callbacks to prevent cascade updates
 * - Automatically cleans up on unmount
 */

import { useEffect, useMemo, useRef, useCallback } from "react";
import { useRealtimeContext, type RealtimeTable } from "@/contexts/RealtimeContext";

interface UseRealtimeSubscriptionOptions {
  /**
   * Debounce delay in milliseconds.
   * Default: 500ms to prevent cascade invalidations.
   */
  debounceMs?: number;
  /**
   * Whether the subscription is enabled.
   * Useful for conditional subscriptions.
   */
  enabled?: boolean;
}

function getTableKey(tables: RealtimeTable | RealtimeTable[]): string {
  return Array.isArray(tables) ? tables.join("|") : tables;
}

function toTableList(tables: RealtimeTable | RealtimeTable[]): RealtimeTable[] {
  return Array.isArray(tables) ? [...tables] : [tables];
}

function useStableRealtimeTables(tables: RealtimeTable | RealtimeTable[]) {
  const tableKey = getTableKey(tables);

  // Stable list identity that only changes when the derived key changes.
  // useMemo replaces the previous ref-cache (react-hooks/refs, TES-1064):
  // same observable behavior — a fresh array exactly when tableKey changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: keyed on tableKey, tables is the source for tableKey
  const tableList = useMemo(() => toTableList(tables), [tableKey]);

  return { tableKey, tableList };
}

/**
 * Subscribe to realtime updates for specific tables with automatic debouncing.
 *
 * @param tables - Single table name or array of table names to subscribe to
 * @param onEvent - Callback to invoke when any of the tables change
 * @param options - Configuration options
 */
export function useRealtimeSubscription(
  tables: RealtimeTable | RealtimeTable[],
  onEvent: () => void,
  options: UseRealtimeSubscriptionOptions = {},
) {
  const { debounceMs = 500, enabled = true } = options;
  const context = useRealtimeContext();
  const subscribe = context?.subscribe;
  const { tableKey, tableList } = useStableRealtimeTables(tables);

  // Keep callback ref stable. Assigning in a commit-phase effect (not during render)
  // satisfies react-hooks/refs (TES-1064) and is behavior-neutral: the ref is only
  // ever read inside the debounced timeout, which fires after commit.
  const callbackRef = useRef(onEvent);
  useEffect(() => {
    callbackRef.current = onEvent;
  }, [onEvent]);

  // Debounce timer ref
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced callback
  const debouncedCallback = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      callbackRef.current();
      timerRef.current = null;
    }, debounceMs);
  }, [debounceMs]);

  useEffect(() => {
    if (!enabled || !subscribe) return;

    const unsubscribe = subscribe(tableList, debouncedCallback);

    return () => {
      unsubscribe();
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [tableKey, tableList, subscribe, debouncedCallback, enabled]);
}
